// components/project/economic/QeSummaryChart.jsx
// V2.1 — Grafico principale dashboard direzionale QE (polish finale).
// UN SOLO bar chart verticale compatto: Venduto / Affidato / Consuntivato / Pagato / Incassato.
// Valori compatti sopra ogni barra; tooltip con valore completo (centesimi) + micro-copy.
// Usa gli STESSI valori certificati dei KPI (nessun ricalcolo interno).

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, LabelList,
} from 'recharts';

const fmtEUR = (v) => Number(v || 0).toLocaleString('it-IT', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
});

// Etichetta sopra barra: compatta, senza centesimi, separatore migliaia sempre (€5.000)
const labelEUR = (v) => `€${Number(v || 0).toLocaleString('it-IT', { maximumFractionDigits: 0, useGrouping: 'always' })}`;

// Asse Y: compatto k/M
const axisEUR = (v) => {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `€${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `€${Math.round(n / 1_000)}k`;
  return `€${Math.round(n)}`;
};

const BAR_COLORS = {
  Venduto: '#10b981',
  Affidato: '#f59e0b',
  Consuntivato: '#FF7819',
  Pagato: '#ef4444',
  Incassato: '#0ea5e9',
};

const DESCRIPTIONS = {
  Venduto: 'Ricavi cliente',
  Affidato: 'Impegni contrattuali',
  Consuntivato: 'Costi certi',
  Pagato: 'Uscite effettuate',
  Incassato: 'Entrate effettive',
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 shadow-sm text-xs" data-testid="qe-chart-tooltip">
      <div className="font-semibold text-slate-700">{p.name}</div>
      <div className="text-[11px] text-slate-500">{DESCRIPTIONS[p.name] || ''}</div>
      <div className="font-mono text-slate-900 mt-0.5">{fmtEUR(p.value)}</div>
    </div>
  );
};

const QeSummaryChart = ({ venduto, affidato, consuntivato, pagato, incassato }) => {
  const data = [
    { name: 'Venduto', value: Number(venduto || 0) },
    { name: 'Affidato', value: Number(affidato || 0) },
    { name: 'Consuntivato', value: Number(consuntivato || 0) },
    { name: 'Pagato', value: Number(pagato || 0) },
    { name: 'Incassato', value: Number(incassato || 0) },
  ];

  return (
    <div className="w-full" data-testid="qe-summary-chart" style={{ height: 230 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 22, right: 12, left: 0, bottom: 2 }} barCategoryGap="18%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDEDED" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: '#525252' }}
            interval={0}
            axisLine={{ stroke: '#E5E5E5' }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={axisEUR}
            tick={{ fontSize: 11, fill: '#737373' }}
            width={52}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={64} isAnimationActive={false}>
            <LabelList dataKey="value" position="top" formatter={labelEUR} fill="#3C3C3C" fontSize={11} fontWeight={600} />
            {data.map((d) => (
              <Cell key={d.name} fill={BAR_COLORS[d.name] || '#94a3b8'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default QeSummaryChart;
